import { useEffect, useMemo, useState } from "react";
import { Search, CreditCard, Smartphone, Laptop, Tablet, Watch, Heart, Headphones } from "lucide-react";
import { HPrimeIcon } from "./HPrimeIcon";
import { HPrimeFilters, matchesAcc } from "./HPrimeFilters";
import { ProductCard } from "./ProductCard";
import { CompactProductCard } from "./CompactProductCard";
import { useIsPhone } from "@/hooks/use-phone";
import { useFavorites } from "@/lib/favorites";
import { Reveal, Eyebrow } from "./Reveal";
import { waLink, CATEGORIES, categoryOf, byModel, isNewCond } from "@/lib/store";
import { useRates } from "@/lib/pricing";

const ICONS = { iphone: Smartphone, macbook: Laptop, ipad: Tablet, watch: Watch, airpods: Headphones, acessorios: HPrimeIcon };

const FILTERS = [
  { id: "todos", label: "Todos" },
  { id: "novo", label: "Novos" },
  { id: "seminovo", label: "Seminovos" },
];

const SORTS = {
  modelo: (a, b) => (categoryOf(a) === "acessorios" ? 0 : byModel(a, b)),
  recentes: () => 0,
  menor: (a, b) => (a.price || Infinity) - (b.price || Infinity),
  maior: (a, b) => (b.price || 0) - (a.price || 0),
};

export const Catalog = ({ products, loading }) => {
  const [category, setCategory] = useState(() => (new URLSearchParams(window.location.search).get("cond") === "acessorios" ? "acessorios" : "iphone"));
  const [filter, setFilter] = useState(() => {
    const c = new URLSearchParams(window.location.search).get("cond");
    return c === "novo" || c === "seminovo" ? c : "todos";
  });

  useEffect(() => {
    const onFilter = (e) => {
      if (e.detail === "acessorios") {
        setCategory("acessorios");
        return;
      }
      setFilter(e.detail);
      setCategory((c) => (c === "acessorios" ? "iphone" : c));
    };
    window.addEventListener("rc-filter", onFilter);
    return () => window.removeEventListener("rc-filter", onFilter);
  }, []);
  const [query, setQuery] = useState("");
  const phone = useIsPhone();
  useRates();

  useEffect(() => {
    const box = document.querySelector("[data-testid=category-tabs]");
    const tab = box?.querySelector(`[data-testid=category-tab-${category}]`);
    if (box && tab) box.scrollTo({ left: tab.offsetLeft - 20, behavior: "smooth" });
  }, [category]);
  const [sort, setSort] = useState("modelo");
  const [acc, setAcc] = useState({ type: "todos", model: "", watt: "" });
  const isAcc = category === "acessorios";
  const isFav = category === "favoritos";
  const favs = useFavorites();

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products
      .filter((p) => (isFav ? favs.includes(p.id) : categoryOf(p) === category))
      .filter((p) => (isAcc ? matchesAcc(p, acc) : isFav || filter === "todos" || (filter === "novo" ? isNewCond(p.condition) : p.condition === filter)))
      .filter((p) => !q || `${p.model} ${p.storage} ${p.color} ${p.wattage || ""} ${(p.compatible || []).join(" ")}`.toLowerCase().includes(q))
      .slice()
      .sort(SORTS[sort]);
  }, [products, category, isAcc, isFav, favs, acc, filter, query, sort]);
  const current = CATEGORIES.find((c) => c.id === category);
  const count = (id) => products.filter((p) => categoryOf(p) === id).length;
  const favCount = products.filter((p) => favs.includes(p.id)).length;

  return (
    <section id="estoque" data-testid="catalog-section" className="relative mx-auto max-w-[1440px] px-5 sm:px-8 py-24 sm:py-36">
      <Reveal className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Eyebrow>(01) — Estoque</Eyebrow>
          <h2 className="font-display mt-5 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-[-0.04em] text-white leading-[0.95]">
            Disponíveis <span className="text-outline">agora.</span>
          </h2>
        </div>
        <div className="max-w-sm">
          <span data-testid="catalog-installments-badge" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm font-bold text-black">
            <CreditCard className="h-4 w-4" /> Parcelamos em até 21x
          </span>
          <p className="mt-4 text-base text-zinc-400">Só aparece aqui o que está na loja. Escolheu? Toque em “Tenho interesse” e a mensagem já chega pronta no nosso WhatsApp.</p>
        </div>
      </Reveal>

      <div data-testid="category-tabs" className="no-scrollbar -mx-5 mt-12 flex gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0">
        {CATEGORIES.map((c) => {
          const Icon = ICONS[c.id];
          const active = c.id === category;
          return (
            <button key={c.id} data-testid={`category-tab-${c.id}`} onClick={() => setCategory(c.id)}
              className={`group flex shrink-0 items-center gap-2.5 rounded-full border px-5 py-3 text-sm font-semibold transition-colors duration-300 ${active ? "border-white bg-white text-black" : "border-white/15 text-zinc-200 hover:border-white/40 hover:text-white"}`}>
              <Icon className="h-4 w-4 transition-transform duration-500 group-hover:-rotate-12" strokeWidth={1.8} />
              {c.label}
              <span className={`mono rounded-full px-2 py-0.5 text-[11px] ${active ? "bg-black text-white" : "bg-white/10 text-zinc-300"}`}>{count(c.id)}</span>
            </button>
          );
        })}
        <button data-testid="category-tab-favoritos" onClick={() => setCategory("favoritos")}
          className={`group flex shrink-0 items-center gap-2.5 rounded-full border px-5 py-3 text-sm font-semibold transition-colors duration-300 ${isFav ? "border-white bg-white text-black" : "border-white/15 text-zinc-200 hover:border-white/40 hover:text-white"}`}>
          <Heart className={`h-4 w-4 ${favCount ? "fill-red-500 text-red-500" : ""}`} strokeWidth={1.8} />
          Favoritos
          <span data-testid="favorites-count" className={`mono rounded-full px-2 py-0.5 text-[11px] ${isFav ? "bg-black text-white" : "bg-white/10 text-zinc-300"}`}>{favCount}</span>
        </button>
      </div>

      {isAcc && <HPrimeFilters items={products.filter((p) => categoryOf(p) === "acessorios")} value={acc} onChange={setAcc} />}

      <div className="mt-4 flex flex-col gap-4 border-y border-white/10 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div className={`flex gap-1 rounded-full border border-white/10 p-1 w-fit ${isAcc || isFav ? "hidden" : ""}`}>
          {FILTERS.map((f) => (
            <button key={f.id} data-testid={`filter-tab-${f.id}`} onClick={() => setFilter(f.id)}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${filter === f.id ? "bg-white text-black" : "text-zinc-300 hover:text-white"}`}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <label className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2.5 focus-within:border-white/40 transition-colors">
            <Search className="h-4 w-4 text-zinc-400" />
            <input data-testid="catalog-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar modelo, cor…" className="w-full sm:w-56 bg-transparent text-sm text-white placeholder:text-zinc-500 outline-none" />
          </label>
          <select data-testid="catalog-sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-full border border-white/10 bg-transparent px-4 py-2.5 text-sm text-zinc-200 outline-none focus:border-white/40">
            <option value="modelo" className="bg-zinc-900">Modelo (mais novo primeiro)</option>
            <option value="recentes" className="bg-zinc-900">Mais recentes</option>
            <option value="menor" className="bg-zinc-900">Menor preço</option>
            <option value="maior" className="bg-zinc-900">Maior preço</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-[3/5] animate-pulse rounded-[20px] bg-white/5 sm:aspect-[4/6] sm:rounded-[28px]" />)}
        </div>
      ) : list.length ? (
        <div data-testid="catalog-grid" className="mt-8 grid gap-3 sm:mt-12 grid-cols-2 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
          {list.map((p, i) => (phone
            ? <CompactProductCard key={p.id} p={p} index={i} preferredModel={isAcc ? acc.model : ""} />
            : <ProductCard key={p.id} p={p} index={i} preferredModel={isAcc ? acc.model : ""} />))}
        </div>
      ) : isFav ? (
        <div data-testid="favorites-empty" className="mt-12 rounded-[28px] border border-dashed border-white/15 px-6 py-16 text-center">
          <Heart className="mx-auto h-9 w-9 text-red-500" strokeWidth={1.6} />
          <p className="font-display mt-4 text-2xl font-bold text-white sm:text-3xl">Nenhum favorito ainda.</p>
          <p className="mt-3 text-zinc-400">Toque no coração de um aparelho para salvar e ver depois.</p>
        </div>
      ) : (
        <div data-testid="catalog-empty" className="mt-12 rounded-[28px] border border-dashed border-white/15 px-6 py-20 text-center">
          <p className="font-display text-3xl font-bold text-white">{count(category) ? "Nada por aqui com esse filtro." : `Sem ${current?.plural} no momento.`}</p>
          <p className="mt-3 text-zinc-400">Procurando algo específico? A gente te ajuda a encontrar.</p>
          <a data-testid="catalog-empty-whatsapp" href={waLink(`Olá, RC iPhones! Estou procurando ${current?.plural}, vocês têm disponível?`)} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex rounded-full bg-white px-6 py-3 text-sm font-semibold text-black">Perguntar no WhatsApp</a>
        </div>
      )}
    </section>
  );
};
